import Vue from 'vue';
import { auth } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	CursorWalk,
	ListFetchFailure,
	ListFetchResult,
	MIRROR_LAG_REFETCH_MS,
	WalkFooterPagination,
	canWalkBack,
	canWalkForward,
	fetchFailureKeepsRows,
	listFetchFailure,
	listFetchFailureMessage,
	restartWalk,
	walkAfterPage,
	walkBack,
	walkFooterPageText,
	walkFooterPagination,
	walkForward,
} from '@/utils/cursorWalk';
import { MirrorLagChannel } from '@/utils/mirrorLagChannel';

/**
 * Paging a Postgres-backed table, the way the Directory does it: Postgres (through
 * api2) decides which rows exist and in what order, a page at a time, and every
 * row then binds its own Firestore document and stays live. Shared by the
 * company's Teams, a Team's members and a member's Teams.
 *
 * Cursor paged, like every api2 listing: the endpoint says "there is more" but
 * never how many, so the table's own footer is off and the same footer, fed
 * from the walk, takes its place (see cursorWalk.ts). Any search, filter, sort
 * or page-size change starts the walk over, because a cursor belongs to one
 * listing and the endpoint refuses it in any other.
 *
 * The host supplies:
 *  - `options` (data): `{ itemsPerPage, sortBy, sortDesc }`;
 *  - `listingReady` (computed): whether there is anything to ask for yet;
 *  - `listingPending` (computed, optional): the reason it is not ready is still
 *    loading, so the table shows its spinner rather than an empty state;
 *  - `fetchListPage({ idToken, cursor })` (method): one page, as a ListFetchResult;
 *  - `listFetchFailureMessages` (computed): the snackbar line for each failure
 *    reason (`unauthorized`, `forbidden`, `network`, `failed`);
 *  - `onListRowsShown(rows)` (method, optional): called with every page shown;
 *  - `listChangeChannel` (computed, optional): the mirror-lag channel for additions
 *    to what the table lists (`TEAMS_ADDED`, teamsMirrorLag.ts). Something added
 *    anywhere in the app refetches the table a mirror lag later, and so does
 *    arriving before the lag has passed. An edit refetches nothing: the row follows
 *    it through its binding and stays where it is, out of sort or out of filter,
 *    until Refresh.
 */
export const CursorListMixin = Vue.extend({
	data () {
		return {
			rows: [] as unknown[],
			// Where the walk is. Every filter, search, sort or page-size change restarts it.
			walk: restartWalk() as CursorWalk,
			// The walk and page size that produced the rows on screen. The footer
			// describes those rows, so while the next page loads it keeps saying
			// "1-25 of many" instead of numbering a page that has not arrived.
			shownPage: {
				walk: restartWalk(),
				itemsPerPage: 0,
			} as { walk: CursorWalk; itemsPerPage: number },
			loading: false,
			failed: false,
			// Bumped on every request so a slow, superseded response is dropped
			// instead of overwriting a newer page.
			requestVersion: 0,
			fetchTimer: null as number | null,
			// True from a user-initiated fetch (page, sort, filter) until a fetch
			// completes. A background refetch that supersedes one answers for it.
			foregroundFetchPending: false,
			// The one pending catch-up refetch after a change, if any.
			mirrorLagRefetchTimer: null as number | null,
			// Set on the way out. TeamPage's dialogs outlive the table and hold
			// its onSuccess callbacks, so one can still be called afterwards.
			isDestroyed: false,
			stopListeningToListChanges: null as (() => void) | null,
		};
	},
	computed: {
		listItemsPerPage (): number {
			return Number(this.options && this.options.itemsPerPage) || 25;
		},
		canWalkBack (): boolean {
			return canWalkBack(this.walk);
		},
		footerPagination (): WalkFooterPagination {
			return walkFooterPagination(this.shownPage.walk, this.shownPage.itemsPerPage || this.listItemsPerPage, this.rows.length);
		},
		// The footer reads the page and the page size, and hands back a copy with
		// one of them changed. The size is the one picked, so the select shows it at once.
		footerOptions (): { page: number; itemsPerPage: number; sortBy: string[]; sortDesc: boolean[] } {
			return {
				...this.options,
				page: this.footerPagination.page,
				itemsPerPage: this.listItemsPerPage,
			};
		},
		footerPageText (): string {
			return walkFooterPageText(this.shownPage.walk, this.footerPagination);
		},
	},
	created () {
		const channel: MirrorLagChannel | null = this.listChangeChannel || null;
		if (!channel) return;
		// Something added anywhere in the app: the list catches up once the mirror has it.
		this.stopListeningToListChanges = channel.onChanged(() => this.refetchAfterMirrorLag());
		// Something added just before this table opened is not in the mirror yet either.
		const remainingMs: number = channel.remainingMs();
		if (remainingMs > 0) this.refetchAfterMirrorLag(remainingMs);
	},
	beforeDestroy () {
		this.isDestroyed = true;
		if (this.fetchTimer !== null) {
			window.clearTimeout(this.fetchTimer);
			this.fetchTimer = null;
		}
		this.clearMirrorLagRefetch();
		if (this.stopListeningToListChanges) {
			this.stopListeningToListChanges();
			this.stopListeningToListChanges = null;
		}
	},
	methods: {
		/**
		 * Coalesce the bursts a table produces (its own mount-time emit, a page
		 * reset followed by the echoed options) into one request. The version
		 * guard in fetchPage covers anything that still overlaps.
		 */
		scheduleFetch () {
			if (this.fetchTimer !== null) return;
			this.fetchTimer = window.setTimeout(() => {
				this.fetchTimer = null;
				this.fetchPage();
			}, 0);
		},
		refetch (): Promise<void> {
			return this.fetchPage();
		},
		/**
		 * Something added, or a row found gone, changes which rows exist, and that
		 * can only catch up once the mirror has the write. At most one timer is
		 * pending: a repeat call restarts it, so the refetch is a full lag after the
		 * latest change.
		 */
		refetchAfterMirrorLag (delayMs: number = MIRROR_LAG_REFETCH_MS) {
			// A dialog on TeamPage can finish after the table is gone. A timer armed
			// then would fetch, and could raise a snackbar, on some other page.
			if (this.isDestroyed) return;
			this.clearMirrorLagRefetch();
			this.mirrorLagRefetchTimer = window.setTimeout(() => {
				this.mirrorLagRefetchTimer = null;
				if (this.isDestroyed) return;
				this.fetchPage(true);
			}, delayMs);
		},
		clearMirrorLagRefetch () {
			if (this.mirrorLagRefetchTimer === null) return;
			window.clearTimeout(this.mirrorLagRefetchTimer);
			this.mirrorLagRefetchTimer = null;
		},
		/**
		 * `background` is the catch-up refetch nobody asked for: if it fails the
		 * table keeps the rows it has (they are live through their bindings) and
		 * only the snackbar says so. Unless it superseded a fetch the user did
		 * ask for: then it answers for that one, and fails as it would have.
		 */
		async fetchPage (background: boolean = false): Promise<void> {
			if (!this.listingReady) {
				// Nothing to ask for. Supersede any request still in flight, or
				// the previous listing's rows would land under this one.
				this.requestVersion++;
				this.foregroundFetchPending = false;
				this.loading = !!this.listingPending;
				this.failed = false;
				this.walk = restartWalk();
				this.showRows([]);
				return;
			}

			const version: number = ++this.requestVersion;
			if (!background) this.foregroundFetchPending = true;
			this.loading = true;
			this.failed = false;

			let result: ListFetchResult<unknown>;
			try {
				const currentUser = auth.currentUser;
				const idToken: string | null = currentUser ? await currentUser.getIdToken() : null;
				result = idToken
					? await this.fetchListPage({ idToken, cursor: this.walk.cursor })
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error loading the list:', e);
				result = { ok: false, reason: 'failed' };
			}

			// A newer request superseded this one; let it own the table. And a
			// table that is gone owns nothing.
			if (this.isDestroyed || version !== this.requestVersion) return;

			this.loading = false;
			const supersededForeground: boolean = this.foregroundFetchPending;
			this.foregroundFetchPending = false;
			const failure: ListFetchFailure | null = listFetchFailure(result);
			if (!failure && result.ok === true) {
				this.walk = walkAfterPage(this.walk, result.page);
				this.showRows(result.page.rows);
				return;
			}

			// The position we resumed from is not in this listing any more, which
			// a remembered cursor can become. Start the walk over rather than
			// telling someone about a cursor they never saw.
			if (failure && failure.reason === 'stale-cursor' && this.walk.cursor) {
				this.walk = restartWalk();
				this.scheduleFetch();
				return;
			}

			if (!fetchFailureKeepsRows({ background, supersededForeground, hasRows: !!this.rows.length })) {
				this.failed = true;
				this.walk = restartWalk();
				this.showRows([]);
			}
			if (failure) this.notifyListFetchFailure(failure);
		},
		// Rows go on screen together with the walk and page size that produced them.
		showRows (rows: unknown[]) {
			this.rows = rows;
			this.shownPage = {
				walk: this.walk,
				itemsPerPage: this.listItemsPerPage,
			};
			if (typeof this.onListRowsShown === 'function') this.onListRowsShown(rows);
		},
		notifyListFetchFailure (failure: ListFetchFailure) {
			// No company or team to read yet: nothing to show and nothing to complain about.
			if (failure.code === 'COMPANY_REQUIRED') return;
			this.$store.dispatch('updateSnackbar', {
				status: SNACKBAR_STATUS.ERROR,
				message: listFetchFailureMessage(failure, this.listFetchFailureMessages || {}),
				show: true,
			});
		},
		/** Back to the start of the walk, and fetch. Every search, filter and sort change does this. */
		resetToFirstPage () {
			this.walk = restartWalk();
			this.scheduleFetch();
		},
		/**
		 * The table hands back its options when a header is clicked, and once when
		 * it mounts. Only the sort is the table's (the footer owns the page and its
		 * size), and a new sort is a new listing, so the walk restarts. Handled
		 * here rather than watched: a sort restored from the store arrives with the
		 * position it belongs to and must not restart it.
		 */
		onTableOptions (next: { sortBy?: string[]; sortDesc?: boolean[] }) {
			const sortBy: string[] = Array.isArray(next.sortBy) ? next.sortBy : [];
			const sortDesc: boolean[] = Array.isArray(next.sortDesc) ? next.sortDesc : [];
			if (JSON.stringify([sortBy, sortDesc]) === JSON.stringify([this.options.sortBy, this.options.sortDesc])) return;
			this.options = { ...this.options, sortBy, sortDesc };
			this.resetToFirstPage();
		},
		setItemsPerPage (itemsPerPage: number) {
			// Restart too: resuming mid-walk with a different size shows a page that
			// starts in an arbitrary place, which reads as rows going missing.
			this.options = { ...this.options, itemsPerPage };
			this.resetToFirstPage();
		},
		goToNextPage () {
			if (!canWalkForward(this.walk)) return;
			this.walk = walkForward(this.walk);
			this.scheduleFetch();
		},
		goToPreviousPage () {
			if (!canWalkBack(this.walk)) return;
			this.walk = walkBack(this.walk);
			this.scheduleFetch();
		},
		/**
		 * The footer hands back its options with one thing changed: the page size
		 * (a new walk from the top), or the page, one either way. A page restored
		 * from the store has no cursors behind it, so its Back starts the walk over
		 * rather than doing nothing.
		 */
		onFooterOptions (next: { page?: number; itemsPerPage?: number }) {
			if (next.itemsPerPage && next.itemsPerPage !== this.footerOptions.itemsPerPage) {
				this.setItemsPerPage(next.itemsPerPage);
				return;
			}
			if (!next.page || next.page === this.footerOptions.page) return;
			if (next.page > this.footerOptions.page) {
				this.goToNextPage();
			} else if (canWalkBack(this.walk)) {
				this.goToPreviousPage();
			} else {
				this.resetToFirstPage();
			}
		},
	},
});
